/* NOCRY theme utilities: money, fetch, layers (drawers and sheets), focus, toast, analytics. */

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const T = () => window.Theme || { routes: {}, settings: {}, strings: {}, shop: {} };

/* ---------- money: cents -> string using the shop's money format ---------- */
export function money(cents, format) {
  const fmt = format || T().shop.moneyFormat || '${{amount}}';
  const n = (Number(cents) || 0) / 100;
  const fixed = (d, sep = '.', th = ',') => {
    const parts = n.toFixed(d).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
    return parts.join(sep);
  };
  let value;
  if (/amount_no_decimals_with_comma_separator/.test(fmt)) value = fixed(0, ',', '.');
  else if (/amount_no_decimals_with_space_separator/.test(fmt)) value = fixed(0, ',', ' ');
  else if (/amount_with_comma_separator/.test(fmt)) value = fixed(2, ',', '.');
  else if (/amount_with_space_separator/.test(fmt)) value = fixed(2, ',', ' ');
  else if (/amount_with_apostrophe_separator/.test(fmt)) value = fixed(2, '.', "'");
  else if (/amount_no_decimals/.test(fmt)) value = fixed(0);
  else value = fixed(2);
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/, value).replace(/<[^>]+>/g, '');
}
export const fmt = (str, vars) => Object.keys(vars || {}).reduce((s, k) => s.split('[' + k + ']').join(vars[k]), str || '');

/* ---------- fetch helpers ---------- */
export async function fetchJSON(url, options = {}) {
  const res = await fetch(url, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.description || data.message || res.statusText); e.data = data; e.status = res.status; throw e; }
  return data;
}
export async function fetchSection(sectionId, url) {
  const u = new URL(url || window.location.href, window.location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error('Section ' + sectionId + ' failed');
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('#shopify-section-' + sectionId) || doc.body;
}
export const debounce = (fn, ms = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
export const getParam = (k) => new URLSearchParams(location.search).get(k);
export function onReady(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn, { once: true }); }
export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- input modality: where focus lands when a layer opens ----------
   Browsers differ on whether a scripted focus() after a mouse click draws the :focus-visible ring (Safari
   does, since it never focuses buttons on click). So the theme tracks the last input itself: keyboard users
   get focus on the first control (ring and all); mouse and touch users get the panel itself, which is
   focusable but has no ring. */
let inputMode = 'pointer';
document.addEventListener('pointerdown', () => { inputMode = 'pointer'; }, { capture: true, passive: true });
document.addEventListener('keydown', (e) => {
  const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) && !/^(Tab|Enter|Escape)$/.test(e.key);
  if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey) inputMode = 'keyboard';
}, { capture: true, passive: true });
export const keyboardUser = () => inputMode === 'keyboard';
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
/** Move focus into an opened panel: its [data-autofocus] field (keyboard, or a fine pointer so phones don't
    get the keyboard popped up), the first control for keyboard users, otherwise the panel itself. */
export function focusInto(panel, first) {
  const auto = panel.querySelector('[data-autofocus]');
  let target = null;
  if (auto && (keyboardUser() || window.matchMedia('(pointer: fine)').matches)) target = auto;
  if (!target && keyboardUser()) target = first || panel.querySelector(FOCUSABLE);
  if (!target) { if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1'); target = panel; }
  target.focus({ preventScroll: true });
}

/* ---------- layers: one open overlay at a time, scroll locked on the root ---------- */
const LAYERS = ['nav', 'cart', 'sheet', 'ask', 'filters', 'img'];
const focusMemory = {};
export const layers = {
  isOpen(name) { return document.body.classList.contains(name + '-open'); },
  open(name, panel) {
    LAYERS.filter((l) => l !== name).forEach((l) => this.close(l, true));
    focusMemory[name] = document.activeElement;
    document.body.classList.add(name + '-open');
    if (panel) {
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('in'));
      setTimeout(() => { if (document.body.classList.contains(name + '-open')) focusInto(panel); }, 30);
      trapFocus(panel);
    }
    document.dispatchEvent(new CustomEvent('nocry:layer:open', { detail: { name } }));
  },
  close(name, silent) {
    if (!this.isOpen(name)) return;
    document.body.classList.remove(name + '-open');
    const panel = document.querySelector('[data-layer="' + name + '"]');
    if (panel) {
      panel.classList.remove('in');
      releaseFocus(panel);
      if (panel.hasAttribute('data-layer-hide')) setTimeout(() => { if (!document.body.classList.contains(name + '-open')) panel.hidden = true; }, 200);
    }
    /* keyboard users go back to what opened the layer; for mouse and touch, focus just leaves the panel (a
       scripted focus on the trigger would draw a ring on it in Safari) */
    const back = focusMemory[name];
    if (!silent) {
      if (keyboardUser() && back && back.focus) back.focus({ preventScroll: true });
      else if (panel && panel.contains(document.activeElement)) document.activeElement.blur();
    }
    document.dispatchEvent(new CustomEvent('nocry:layer:close', { detail: { name } }));
  },
  toggle(name, panel) { this.isOpen(name) ? this.close(name) : this.open(name, panel); },
  closeAll() { LAYERS.forEach((l) => this.close(l)); },
};
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') layers.closeAll(); });
document.addEventListener('click', (e) => { if (e.target.closest('[data-overlay]')) layers.closeAll(); });

function trapFocus(panel) {
  releaseFocus(panel);
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    const items = $$(FOCUSABLE, panel).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  panel.__trap = handler;
  panel.addEventListener('keydown', handler);
}
function releaseFocus(panel) { if (panel.__trap) { panel.removeEventListener('keydown', panel.__trap); panel.__trap = null; } }

/* ---------- toast ---------- */
let toastTimer;
export function toast(msg, ms = 2600) {
  const el = $('[data-toast]');
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { el.hidden = true; }, ms);
}

/* ---------- analytics: dataLayer for GA4/GTM, Shopify custom pixel events for Meta CAPI dedup ---------- */
export function track(event, params = {}) {
  const s = T().settings || {};
  const payload = { ...params };
  if (s.analyticsDatalayer !== false) { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event, ...payload }); }
  try {
    if (s.analyticsPublish !== false && window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('nocry:' + event, payload);
    }
  } catch (e) { /* no-op */ }
  document.dispatchEvent(new CustomEvent('nocry:track', { detail: { event, params: payload } }));
}
export function utm() { const q = new URLSearchParams(location.search); return { utm_content: q.get('utm_content') || '', angle: q.get('h') || 'default' }; }

/* ---------- pack option helpers shared by cards, sheet and PDP ---------- */
export function parseQty(value) { const m = /^\s*(\d+)/.exec(String(value || '')); return m ? parseInt(m[1], 10) : 1; }
/* The word for what a pack counts, for n of them, in the page's language: the unit is the English key (pair, shield,
   kit...), its words come from the locale through Theme.strings.unitWords (snippets/theme-config). A key the locale
   doesn't list prints itself, with an "s" for more than one. */
export function unitWord(n, unit) {
  const key = String(unit || T().settings.unitNoun || 'pair');
  const w = (T().strings.unitWords || {})[key];
  if (w && w.one && w.other) return n === 1 ? w.one : w.other;
  return n === 1 ? key : key + 's';
}
/* The unit key for a product read from the storefront's JSON (/products/<handle>.js, product recommendations), the same
   in every language and the same guess as snippets/unit-key: a `unit:` tag, else the handle (never translated) and the
   title (English words, and the Spanish ones the es title uses), in the old title rule's order, else pair. */
const UNIT_RULES = [
  ['kit', ['kit', '+ mask', 'and face mask', 'mascarilla'], ['kit', '-mask']],
  ['shield', ['face shield', 'visor', 'careta'], ['face-shield', 'visor']],
  ['helmet', ['helmet', 'casco'], ['helmet']],
  ['vest', ['vest', 'chaleco'], ['vest']],
  ['apron', ['apron', 'mandil', 'delantal'], ['apron']],
  ['case', ['case', 'estuche'], ['case']],
  ['pad', ['kneeling pad', 'kneeling mat', 'almohadilla para arrodillarse', 'tapete para arrodillarse'], ['kneeling-pad', 'kneeling-mat']],
];
export function unitKey(p) {
  const tags = Array.isArray(p && p.tags) ? p.tags : String((p && p.tags) || '').split(',');
  const tag = tags.map((x) => String(x).trim()).find((x) => /^unit:/i.test(x));
  if (tag) return tag.slice(5).trim();
  const t = String((p && p.title) || '').toLowerCase(), h = String((p && p.handle) || '').toLowerCase();
  const hit = UNIT_RULES.find(([, tw, hw]) => tw.some((w) => t.includes(w)) || hw.some((w) => h.includes(w)));
  return hit ? hit[0] : 'pair';
}
/* "1 pair", "2 pairs" (products.unit.one / .plural with the word from unitWord) */
export function unitLabel(n, unit) {
  const s = T().strings;
  const w = unitWord(n, unit);
  return n === 1 ? fmt(s.unitsOne || '1 [unit]', { unit: w }) : fmt(s.units || '[n] [unit]', { n, unit: w });
}
export function setRootVar(name, value) { document.documentElement.style.setProperty(name, value); }
